"use client";

import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, CalendarCheck, CircleCheck as CheckCircle2, Clock, Compass, ExternalLink, CircleQuestionMark as HelpCircle, Layers, Lightbulb, Rocket, Target, Trophy } from 'lucide-react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare const wvcClient: any;
const getWvcClient = () =>
  typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;

function ProgramContent() {
  const { posts, loading, error } = usePosts();
  const post = posts && posts.length > 0 ? posts[0] : null;
  const cf = post?.customFields || {};

  // Language state synchronized with global website preference
  const [lang, setLang] = useState<"vi" | "en">(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("roboto_lang");
      if (saved === "vi" || saved === "en") return saved;
    }
    return "vi";
  });

  useEffect(() => {
    const handleLangChange = (e: any) => {
      const nextLang = e?.detail?.lang || localStorage.getItem("roboto_lang");
      if (nextLang === "vi" || nextLang === "en") {
        setLang(nextLang);
      }
    };
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "roboto_lang" && (e.newValue === "vi" || e.newValue === "en")) {
        setLang(e.newValue);
      }
    };

    window.addEventListener("roboto-lang-change", handleLangChange);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const t = (vi: string, en: string) => (lang === "vi" ? vi : en);

  // Extract CPT fields with defaults
  const programTitle = cf.program_name || post?.title || "Robotics Engineering & Autonomous Systems";
  const tagline =
    cf.tagline ||
    "Hands-on mechanical prototyping, algorithmic motor controls, and Python-driven robotics for aspiring creators.";
  const ageRange = cf.age_range || "7–12 YRS";
  const level = cf.level || "Intermediate Track";
  const whoItsFor =
    cf.who_its_for ||
    "Designed for motivated young innovators who have moved past basic building blocks and want to build, wire, and program real autonomous machines.";

  // Parse multi-line or delimited text fields
  const learningJourneyPhases = useMemo(() => {
    if (cf.learning_journey) {
      return cf.learning_journey
        .split("\n")
        .map((line: string) => line.trim())
        .filter((line: string) => line.length > 0);
    }
    return [
      "Phase 1: Mechanical Blueprints & Gear Ratios",
      "Phase 2: Sensor Calibration & Microcontroller Wiring",
      "Phase 3: Algorithmic Logic & Autonomous Navigation",
      "Phase 4: Capstone Arena Trials & Defense Strategy",
    ];
  }, [cf.learning_journey]);

  const outcomesList = useMemo(() => {
    if (cf.outcomes) {
      return cf.outcomes
        .split("\n")
        .map((line: string) => line.trim())
        .filter((line: string) => line.length > 0);
    }
    return [
      "Master mechanical drive-trains, rack-and-pinion, and torque conversion",
      "Write clean sequential Python scripts to read ultrasonic and gyro sensors",
      "Diagnose hardware-software anomalies using systematic lab test procedures",
      "Document prototyping iterations in an engineering design journal",
      "Deploy fail-safe algorithms for real-time robotic obstacle avoidance",
      "Present technical builds with confident scientific articulation",
    ];
  }, [cf.outcomes]);

  const projectSteps = useMemo(() => {
    if (cf.project_based_steps) {
      return cf.project_based_steps
        .split("\n")
        .map((line: string) => line.trim())
        .filter((line: string) => line.length > 0);
    }
    return [
      "Chassis Blueprint & Rapid Structural Assembly",
      "Actuator Integration & Direct Power Distribution",
      "Embedded Logic Programming & Sensor Calibration",
      "Autonomous Speed Run & Obstacle Defense Test",
    ];
  }, [cf.project_based_steps]);

  const skillsList = useMemo(() => {
    if (cf.skills) {
      return cf.skills
        .split(",")
        .map((item: string) => item.trim())
        .filter((item: string) => item.length > 0);
    }
    return [
      "Mechanical Design",
      "Python Scripting",
      "Sensor Integration",
      "Algorithmic Thinking",
      "CAD Modeling",
      "Debugging",
      "Spatial Reasoning",
      "Rapid Prototyping",
    ];
  }, [cf.skills]);

  const roadmapSteps = useMemo(() => {
    if (cf.roadmap) {
      return cf.roadmap
        .split("\n")
        .map((line: string) => line.trim())
        .filter((line: string) => line.length > 0);
    }
    return [
      "Level 1: Fundamentals of Mechanical Kinematics",
      "Level 2: Autonomous Logic & Sensor Integration",
      "Level 3: National Arena & Competition Preparation",
    ];
  }, [cf.roadmap]);

  const parentMilestones = useMemo(() => {
    if (cf.parent_milestones) {
      return cf.parent_milestones
        .split("\n")
        .map((line: string) => line.trim())
        .filter((line: string) => line.length > 0);
    }
    return [
      "Week 4: First Autonomous Line-Follower Demonstration",
      "Week 8: Mid-term Engineering Presentation & Code Audit",
      "Week 12: Arena Performance Assessment & Robotics Certificate",
    ];
  }, [cf.parent_milestones]);

  const faqs = useMemo(() => {
    const list: { q: string; a: string }[] = [];
    if (cf.faq_question_1 && cf.faq_answer_1) list.push({ q: cf.faq_question_1, a: cf.faq_answer_1 });
    if (cf.faq_question_2 && cf.faq_answer_2) list.push({ q: cf.faq_question_2, a: cf.faq_answer_2 });
    if (cf.faq_question_3 && cf.faq_answer_3) list.push({ q: cf.faq_question_3, a: cf.faq_answer_3 });
    if (cf.faq_question_4 && cf.faq_answer_4) list.push({ q: cf.faq_question_4, a: cf.faq_answer_4 });

    if (list.length === 0) {
      if (lang === "vi") {
        return [
          {
            q: "Con tôi chưa từng học lập trình hay chế tạo có tham gia được không?",
            a: "Hoàn toàn không cần kinh nghiệm trước. Chúng tôi sẽ đánh giá tư duy của con trong buổi học thử đầu tiên và xây dựng lộ trình tiếp cận từ cơ bản đến chuyên sâu theo đúng năng lực của bé.",
          },
          {
            q: "Trang thiết bị và linh kiện thực hành tại phòng Lab gồm những gì?",
            a: "Toàn bộ bộ kit robotics tiêu chuẩn quốc tế, vi điều khiển, máy trạm kỹ thuật màn hình kép và trang thiết bị an toàn đều được trang bị đầy đủ tại các phòng lab của học viện.",
          },
          {
            q: "Phụ huynh theo dõi sự tiến bộ và kết quả học tập của con như thế nào?",
            a: "Phụ huynh nhận báo cáo hồ sơ học tập số định kỳ 4 tuần một lần, bao gồm video cơ chế vận hành thực tế, mã nguồn dự án và nhận xét đánh giá chi tiết từ giảng viên cố vấn.",
          },
          {
            q: "Học viên có cơ hội tham gia các giải đấu robotics chuyên nghiệp không?",
            a: "Có. Học viên đạt chuẩn năng lực vượt trội sẽ được định hướng và huấn luyện trực tiếp để tham gia đội tuyển thi đấu cấp quốc gia và quốc tế của học viện.",
          },
        ];
      }
      return [
        {
          q: "Does my child need prior coding or engineering experience?",
          a: "No prior experience is necessary. We assess each student on their first trial session and scaffold hardware and programming modules based on individual readiness.",
        },
        {
          q: "What equipment or hardware is provided in the lab?",
          a: "All professional robotics kits, microcontrollers, dual-screen engineering workstations, and safety equipment are fully supplied within our academy labs.",
        },
        {
          q: "How are parents informed about learning milestones?",
          a: "Parents receive digital portfolio updates every 4 weeks including verified code repositories, mechanism video recordings, and mentor feedback assessments.",
        },
        {
          q: "Can students participate in competitive tournaments?",
          a: "Yes. Students demonstrating milestone mastery receive direct pathways into our national and regional robotics competition teams.",
        },
      ];
    }
    return list;
  }, [cf, lang]);

  // Image wrapper for CPT hero image
  const resolvedHeroImage =
    (typeof cf.hero_image === "string" ? cf.hero_image : cf.hero_image?.url) ||
    post?.featuredImage;

  const heroImg = {
    src: resolvedHeroImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_lab_hero.png",
    srcset: resolvedHeroImage ? "" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_lab_hero.png-wvc-srcset",
    sizes: resolvedHeroImage ? "" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_lab_hero.png-wvc-sizes",
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-20">
        <div className="h-10 w-2/3 animate-pulse rounded-lg bg-muted" />
        <div className="mt-4 h-6 w-1/3 animate-pulse rounded-lg bg-muted" />
        <div className="mt-8 h-96 w-full animate-pulse rounded-xl bg-muted" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16 text-center">
        <p className="text-muted-foreground">
          {t("Hiện chưa thể tải thông tin chi tiết chương trình.", "Unable to load program details at this moment.")}
        </p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/ProgramsPage">
            {t("Xem tất cả chương trình", "View all programs")}
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-16 md:gap-24">
      {/* 1. Hero Section */}
      <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-md md:p-12 lg:p-16">
        <div className="relative z-10 grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
          <div className="flex flex-col gap-6 lg:col-span-7">
            {/* Lab Blueprint spec notch line */}
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-none bg-secondary" />
              <span className="h-[1px] w-12 bg-secondary" />
              <span className="font-sans text-xs font-bold uppercase tracking-widest text-secondary">
                {t("Bản Thiết Kế Chương Trình", "Curriculum Blueprint")}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2.5">
              <Badge
                variant="outline"
                className="rounded-full border-secondary/40 bg-secondary/10 px-3 py-1 font-sans text-xs font-bold uppercase text-secondary"
              >
                {ageRange}
              </Badge>
              <Badge
                variant="default"
                className="rounded-md bg-primary px-3 py-1 font-sans text-xs font-semibold text-primary-foreground"
              >
                {level}
              </Badge>
            </div>

            <h1 className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-5xl">
              {programTitle}
            </h1>

            <p className="font-sans text-base text-muted-foreground sm:text-lg">
              {tagline}
            </p>

            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Button
                asChild
                size="lg"
                className="bg-accent font-serif font-semibold text-accent-foreground shadow-sm hover:bg-accent/90"
              >
                <Link to="/FreeTrialContactPage">
                  {t("Đăng Ký Học Thử", "Book Free Trial")}
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-primary font-serif font-semibold text-primary hover:bg-primary hover:text-primary-foreground"
              >
                <Link to="/FreeTrialContactPage">
                  {t("Đăng Ký Tư Vấn", "Request Consultation")}
                </Link>
              </Button>
            </div>
          </div>

          <div className="relative lg:col-span-5">
            <div className="relative aspect-4/3 w-full overflow-hidden rounded-xl border border-border bg-muted shadow-sm">
              <img
                src={heroImg.src}
                data-wvc-srcset={heroImg.srcset}
                data-wvc-sizes={heroImg.sizes}
                alt={programTitle}
                loading="lazy"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </div>

      {/* 2. Who It's For */}
      <div className="rounded-xl border-l-4 border-l-primary bg-card p-6 shadow-sm md:p-8">
        <div className="flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" />
          <h2 className="font-serif text-2xl font-bold tracking-tight text-foreground">
            {t("Đối tượng phù hợp", "Who it's for")}
          </h2>
        </div>
        <p className="mt-3 font-sans text-base leading-relaxed text-muted-foreground md:text-lg">
          {whoItsFor}
        </p>
      </div>

      {/* 3. Learning Journey & Project-Based Steps */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Learning Journey */}
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm md:p-8">
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted text-primary">
              <Compass className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-serif text-xl font-bold text-foreground">
                {t("Hành trình học tập", "Learning journey")}
              </h3>
              <p className="font-sans text-xs uppercase tracking-wider text-muted-foreground">
                {t("Tiến trình từng giai đoạn bài bản", "Systematic phase progression")}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {learningJourneyPhases.map((phase: string, i: number) => (
              <div
                key={i}
                data-index={i}
                className="flex items-start gap-4 rounded-lg border border-border/80 bg-background p-4"
              >
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary/15 font-sans text-xs font-bold text-secondary">
                  0{i + 1}
                </div>
                <p className="font-sans text-sm font-medium text-foreground">
                  {phase}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Project-Based Steps */}
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm md:p-8">
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted text-primary">
              <Layers className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-serif text-xl font-bold text-foreground">
                {t("Các bước dự án thực tế", "Project-based steps")}
              </h3>
              <p className="font-sans text-xs uppercase tracking-wider text-muted-foreground">
                {t("Từ linh kiện đến cỗ máy vận hành", "From component to operational machine")}
              </p>
            </div>
          </div>

          <ol className="relative border-l border-border pl-6">
            {projectSteps.map((step: string, i: number) => (
              <li key={i} data-index={i} className="mb-6 last:mb-0">
                <span className="absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full bg-primary font-sans text-xs font-bold text-primary-foreground">
                  {i + 1}
                </span>
                <h4 className="font-serif text-base font-semibold text-foreground">
                  {t(`Giai đoạn ${i + 1}`, `Stage ${i + 1}`)}
                </h4>
                <p className="mt-1 font-sans text-sm text-muted-foreground">
                  {step}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* 4. Outcomes (Checklist Grid) */}
      <div className="rounded-xl border border-border bg-card p-6 shadow-sm md:p-8 lg:p-10">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted text-primary">
            <CheckCircle2 className="h-5 w-5 text-secondary" />
          </div>
          <div>
            <h2 className="font-serif text-2xl font-bold text-foreground">
              {t("Chuẩn đầu ra năng lực", "Mastery outcomes")}
            </h2>
            <p className="font-sans text-sm text-muted-foreground">
              {t(
                "Năng lực kỹ thuật cụ thể học viên tự tin làm chủ và trình diễn.",
                "Concrete technical competencies every graduate can demonstrate."
              )}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {outcomesList.map((outcome: string, i: number) => (
            <div
              key={i}
              data-index={i}
              className="flex items-start gap-3 rounded-lg border border-border bg-background p-4"
            >
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-secondary" />
              <span className="font-sans text-sm text-foreground">
                {outcome}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* 5. Skills Tag Cloud & 3-Step Roadmap */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        {/* Skills Tag Cloud */}
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm md:p-8 lg:col-span-5">
          <div className="mb-5 flex items-center gap-3">
            <Lightbulb className="h-5 w-5 text-accent" />
            <h3 className="font-serif text-xl font-bold text-foreground">
              {t("Kỹ năng ứng dụng", "Applied skills")}
            </h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {skillsList.map((skill: string, i: number) => (
              <span
                key={i}
                data-index={i}
                className="rounded-md border border-border bg-muted/60 px-3 py-1.5 font-sans text-xs font-semibold text-foreground transition-colors hover:border-secondary hover:bg-secondary/10"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>

        {/* 3-Step Roadmap Progression Bar */}
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm md:p-8 lg:col-span-7">
          <div className="mb-6 flex items-center gap-3">
            <Rocket className="h-5 w-5 text-primary" />
            <h3 className="font-serif text-xl font-bold text-foreground">
              {t("Lộ trình 3 bước", "Three-step roadmap")}
            </h3>
          </div>

          <div className="relative grid grid-cols-1 gap-4 md:grid-cols-3">
            {roadmapSteps.map((step: string, i: number) => (
              <div
                key={i}
                data-index={i}
                className="relative flex flex-col justify-between rounded-lg border border-border bg-background p-4"
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-sans text-xs font-bold text-secondary">
                    {t(`BƯỚC 0${i + 1}`, `STEP 0${i + 1}`)}
                  </span>
                  {i < roadmapSteps.length - 1 && (
                    <ArrowRight className="hidden h-4 w-4 text-muted-foreground md:block" />
                  )}
                </div>
                <p className="font-sans text-sm font-medium text-foreground">
                  {step}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 6. Related Work Strip */}
      <div className="rounded-xl border border-border bg-muted/40 p-6 md:p-8">
        <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-center">
          <div>
            <div className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-accent" />
              <h3 className="font-serif text-xl font-bold text-foreground">
                {t("Dự án tiêu biểu & Đấu trường thi đấu", "Showcase & competition links")}
              </h3>
            </div>
            <p className="mt-1 font-sans text-sm text-muted-foreground">
              {t(
                "Khám phá các cỗ máy học viên chế tạo và thử nghiệm tại các giải đấu toàn quốc.",
                "See what students in this program build and test in national arenas."
              )}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {cf.project_domain_link ? (
              <Button asChild variant="outline" size="sm" className="gap-2">
                <Link to={cf.project_domain_link}>
                  <span>{t("Dự án tiêu biểu", "Project showcase")}</span>
                  <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              </Button>
            ) : (
              <Button asChild variant="outline" size="sm" className="gap-2">
                <Link to="/ProjectsCompetitionsPage">
                  <span>{t("Dự án tiêu biểu", "Project showcase")}</span>
                  <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              </Button>
            )}

            {cf.competition_link ? (
              <Button asChild variant="outline" size="sm" className="gap-2">
                <Link to={cf.competition_link}>
                  <span>{t("Giải đấu & Đấu trường", "Tournament track")}</span>
                  <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              </Button>
            ) : (
              <Button asChild variant="outline" size="sm" className="gap-2">
                <Link to="/StudentAchievementsStoriesPage">
                  <span>{t("Giải đấu & Đấu trường", "Tournament track")}</span>
                  <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* 7. Parent Milestones */}
      <div>
        <div className="mb-6 flex items-center gap-3">
          <CalendarCheck className="h-6 w-6 text-primary" />
          <div>
            <h2 className="font-serif text-2xl font-bold text-foreground">
              {t("Cột mốc dành cho phụ huynh", "Parent milestones")}
            </h2>
            <p className="font-sans text-sm text-muted-foreground">
              {t(
                "Các điểm đánh giá định kỳ và kết quả thực tế gửi tới gia đình suốt khóa học.",
                "Key check-ins and performance checkpoints for families throughout the term."
              )}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {parentMilestones.map((milestone: string, i: number) => (
            <Card key={i} data-index={i} className="border border-border bg-card shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-secondary" />
                  <span className="font-sans text-xs font-bold uppercase tracking-wider text-secondary">
                    {t(`Cột mốc 0${i + 1}`, `Checkpoint 0${i + 1}`)}
                  </span>
                </div>
                <CardTitle className="font-serif text-base font-semibold text-foreground">
                  {milestone.split(":")[0] || t(`Cột mốc ${i + 1}`, `Milestone ${i + 1}`)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-sans text-sm text-muted-foreground">
                  {milestone.split(":")[1]?.trim() || milestone}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* 8. FAQ Accordion */}
      <div className="rounded-xl border border-border bg-card p-6 shadow-sm md:p-8 lg:p-10">
        <div className="mb-6 flex items-center gap-3">
          <HelpCircle className="h-6 w-6 text-primary" />
          <div>
            <h2 className="font-serif text-2xl font-bold text-foreground">
              {t("Câu hỏi thường gặp", "Frequently asked questions")}
            </h2>
            <p className="font-sans text-sm text-muted-foreground">
              {t(
                "Giải đáp chi tiết thắc mắc từ phụ huynh về điều kiện và lộ trình khóa học.",
                "Common questions from parents about course requirements and schedules."
              )}
            </p>
          </div>
        </div>

        <Accordion type="single" collapsible className="w-full">
          {faqs.map((faq, i) => (
            <AccordionItem key={i} data-index={i} value={`faq-${i}`} className="border-border">
              <AccordionTrigger className="font-serif text-base font-semibold text-foreground hover:no-underline">
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="font-sans text-sm leading-relaxed text-muted-foreground">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      {/* 9. Closing CTA Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-primary p-8 text-primary-foreground shadow-lg md:p-12">
        <div className="relative z-10 flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
          <div className="max-w-2xl">
            <h2 className="font-serif text-2xl font-bold tracking-tight sm:text-3xl">
              {t("Sẵn sàng kiểm tra năng lực và xếp lớp cho con?", "Ready to verify your child's placement?")}
            </h2>
            <p className="mt-2 font-sans text-sm text-primary-foreground/90 sm:text-base">
              {t(
                "Đặt lịch trải nghiệm 60 phút tại phòng lab công nghệ. Đội ngũ cố vấn sẽ đánh giá tư duy không gian, khả năng giải quyết vấn đề và tư vấn đúng lộ trình cho con.",
                "Book a 60-minute laboratory assessment. Our mentors evaluate spatial problem-solving and place your child into the exact right track."
              )}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              asChild
              size="lg"
              className="bg-accent font-serif font-semibold text-accent-foreground hover:bg-accent/90"
            >
              <Link to="/FreeTrialContactPage">
                {t("Đăng Ký Học Thử", "Book Free Trial")}
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="border-primary-foreground/30 bg-transparent font-serif font-semibold text-primary-foreground hover:bg-primary-foreground hover:text-primary"
            >
              <Link to="/ProgramsPage">
                {t("Khám phá tất cả chương trình", "Explore all programs")}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Program() {
  const client = getWvcClient();
  const currentWPQueryParams = client?.getCurrentWPQueryParams?.() || {
    post_type: "program",
    per_page: 1,
  };
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section
      id="single-program-template-section"
      data-nav="light"
      className="bg-background py-16 text-foreground md:py-24"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <PostsProvider wp_query={wp_query}>
          <ProgramContent />
        </PostsProvider>
      </div>
    </section>
  );
}